import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Language = 'fr' | 'en';

interface LanguageState {
  language: Language;
  setLanguage: (lang: Language) => void;
}

export const useLanguage = create<LanguageState>()(
  persist(
    (set) => ({
      language: 'fr',
      setLanguage: (lang) => set({ language: lang }),
    }),
    {
      name: 'language-storage',
    }
  )
);

// Traductions
export const translations = {
  fr: {
    // Login
    login: {
      title: 'CEDO Trace',
      subtitle: 'Connectez-vous à votre compte',
      email: 'Email',
      password: 'Mot de passe',
      submit: 'Se connecter',
      noAccount: "Pas encore de compte ?",
      register: "S'inscrire",
      success: 'Connexion réussie !',
      error: 'Erreur de connexion',
      emailRequired: 'Email requis',
      emailInvalid: 'Email invalide',
      passwordRequired: 'Mot de passe requis',
    },
    // Register
    register: {
      title: 'CEDO Trace',
      subtitle: 'Créez votre compte',
      companyInfo: 'Informations Entreprise',
      personalInfo: 'Informations Personnelles',
      accountInfo: 'Informations de Compte',
      companyName: 'Nom de l\'entreprise',
      companyType: 'Type d\'entreprise',
      particulierOnlyNotice: 'L\'auto-inscription en ligne crée un compte Particulier (individu / flotte express). Pour un compte entreprise (e-commerce, transport, fret international...), contactez un administrateur.',
      fullName: 'Nom complet',
      phone: 'Téléphone',
      email: 'Email',
      password: 'Mot de passe',
      confirmPassword: 'Confirmer le mot de passe',
      submit: 'Créer mon compte',
      hasAccount: 'Déjà un compte ?',
      login: 'Se connecter',
      success: 'Compte créé avec succès !',
      error: 'Erreur lors de la création',
      companyNameRequired: 'Nom de l\'entreprise requis',
      companyTypeRequired: 'Type d\'entreprise requis',
      nameRequired: 'Nom complet requis',
      phoneRequired: 'Téléphone requis',
      emailRequired: 'Email requis',
      emailInvalid: 'Email invalide',
      passwordRequired: 'Mot de passe requis',
      passwordMin: 'Minimum 6 caractères',
      confirmPasswordRequired: 'Confirmation requise',
      passwordsNotMatch: 'Les mots de passe ne correspondent pas',
      types: {
        particulier: 'Particulier (P2P)',
        ecommerce: 'Vendeur social (e-commerce informel)',
        ecommerce_structure: 'E-commerçant structuré (PME / marque)',
        transport_interurbain: 'Agence de transport interurbain (autocars)',
        cargo_international: 'Transitaire / cargo international (air & mer)',
        transit_douane: 'Cabinet de transit et de douane',
        inter_pays: 'Transporteur transfrontalier',
        transport_local: 'Société de livraison urbaine & coursiers',
        multisite_distribution: 'Entreprise multisite / distribution',
      },
    },
    // Dashboard
    dashboard: {
      welcome: 'Bienvenue',
      totalColis: 'Total Colis',
      inTransit: 'En Transit',
      inDelivery: 'En Livraison',
      delivered: 'Livrés',
      recentParcels: 'Colis Récents',
      tracking: 'N° Suivi',
      recipient: 'Destinataire',
      city: 'Ville',
      status: 'Statut',
      amount: 'Montant',
      loading: 'Chargement...',
      noParcels: 'Aucun colis pour le moment',
      viewAll: 'Voir tout',
    },
    // Create Colis
    createColis: {
      title: 'Créer un Nouveau Colis',
      senderInfo: 'Informations Expéditeur',
      recipientInfo: 'Informations Destinataire',
      parcelDetails: 'Détails du Colis',
      fullName: 'Nom complet',
      phone: 'Téléphone',
      city: 'Ville',
      fullAddress: 'Adresse complète',
      description: 'Description du contenu',
      value: 'Valeur (FCFA)',
      weight: 'Poids (KG)',
      dimensions: 'Dimensions (LxlxH)',
      deliveryPerson: 'Livreur',
      deliveryPersonOptional: 'Livreur (optionnel)',
      assignLater: '-- Assigner plus tard --',
      assignNowOrLater: 'Vous pouvez assigner un livreur maintenant ou plus tard',
      allCities: 'Toutes villes',
      cancel: 'Annuler',
      create: 'Créer le Colis',
      success: 'Colis créé avec succès ! N°',
      error: 'Erreur lors de la création',
      required: 'requis',
      nameRequired: 'Nom du destinataire requis',
      phoneRequired: 'Téléphone requis',
      cityRequired: 'Ville requise',
      descRequired: 'Description requise',
    },
    // Colis Details
    colisDetails: {
      createdOn: 'Créé le',
      updateStatus: 'Modifier statut',
      assignDelivery: 'Assigner livreur',
      changeDelivery: 'Changer livreur',
      print: 'Imprimer',
      download: 'Télécharger',
      currentStatus: 'Statut Actuel',
      notSpecified: 'Non spécifié',
      sender: 'Expéditeur',
      recipient: 'Destinataire',
      parcelDetails: 'Détails du Colis',
      description: 'Description',
      value: 'Valeur',
      weight: 'Poids',
      dimensions: 'Dimensions',
      totalAmount: 'Montant Total',
      assignedDelivery: 'Livreur Assigné',
      change: 'Changer',
      history: 'Historique',
      noHistory: 'Aucun historique disponible',
      by: 'Par',
      updateStatusTitle: 'Mettre à jour le statut',
      newStatus: 'Nouveau statut',
      selectStatus: '-- Sélectionner --',
      comment: 'Commentaire (optionnel)',
      addComment: 'Ajouter un commentaire...',
      confirm: 'Confirmer',
      updating: 'Mise à jour...',
      parcelNotFound: 'Colis introuvable',
      backToList: 'Retour à la liste',
    },
    // Livreurs
    livreurs: {
      title: 'Livreurs',
      deliveryPersons: 'livreurs',
      addDelivery: 'Ajouter un livreur',
      totalDelivery: 'Total Livreurs',
      active: 'Actifs',
      totalDeliveries: 'Total Livraisons',
      search: 'Rechercher un livreur...',
      view: 'Voir',
      edit: 'Modifier',
      delete: 'Supprimer',
      delivered: 'Livrés',
      failed: 'Échecs',
      rating: 'Note',
      newDelivery: 'Nouveau livreur',
      editDelivery: 'Modifier le livreur',
      fullName: 'Nom complet',
      phone: 'Téléphone',
      email: 'Email',
      mainCity: 'Ville principale',
      isActive: 'Livreur actif',
      cancel: 'Annuler',
      create: 'Créer',
      update: 'Modifier',
      saving: 'Enregistrement...',
      noDeliveryFound: 'Aucun livreur trouvé',
      confirmDelete: 'Supprimer',
      activeStatus: 'Actif',
      inactiveStatus: 'Inactif',
      createSuccess: 'Livreur créé !',
      updateSuccess: 'Livreur modifié !',
      deleteSuccess: 'Livreur supprimé !',
      deleteError: 'Erreur lors de la suppression',
      saveError: 'Erreur lors de la sauvegarde',
    },
    // Livreur Details
    livreurDetails: {
      information: 'Informations',
      telephone: 'Téléphone',
      email: 'Email',
      mainCity: 'Ville principale',
      memberSince: 'Membre depuis',
      performance: 'Performance',
      successRate: 'Taux de réussite',
      averageRating: 'Note moyenne',
      totalParcels: 'Total Colis',
      delivered: 'Livrés',
      failed: 'Échecs',
      assignedParcels: 'Colis assignés',
      noParcelsAssigned: 'Aucun colis assigné',
      modify: 'Modifier',
      deliveryNotFound: 'Livreur introuvable',
      backToList: 'Retour à la liste',
      date: 'Date',
    },
    // Assign Delivery Modal
    assignDelivery: {
      title: 'Assigner un livreur',
      currentDelivery: 'Livreur actuel',
      unassign: 'Désassigner',
      search: 'Rechercher un livreur...',
      noDeliveryFound: 'Aucun livreur trouvé',
      cancel: 'Annuler',
      assign: 'Assigner',
      assigning: 'Assignation...',
      selectDelivery: 'Veuillez sélectionner un livreur',
      confirmUnassign: 'Désassigner le livreur actuel ?',
    },
    // Common
    common: {
      logout: 'Déconnexion',
      dashboard: 'Tableau de bord',
      parcels: 'Colis',
      createParcel: 'Créer un colis',
      tracking: 'Suivi',
      settings: 'Paramètres',
      reports: 'Rapports',
      profile: 'Mon Profil',
      notifications: 'Notifications',
      users: 'Utilisateurs',
      tarifs: 'Tarifs',
      wallet: 'Portefeuille',
      save: 'Enregistrer',
      cancel: 'Annuler',
      delete: 'Supprimer',
      edit: 'Modifier',
      create: 'Créer',
      search: 'Rechercher...',
      loading: 'Chargement...',
      noData: 'Aucune donnée',
      error: 'Une erreur est survenue',
      yes: 'Oui',
      no: 'Non',
      active: 'Actif',
      inactive: 'Inactif',
      actions: 'Actions',
      back: 'Retour',
      listeColis: 'Liste des colis',
      notifHistory: 'Historique & Envoi',
      notifTemplates: 'Templates',
      myProfile: 'Mon Profil',
      lastConnection: 'Dernière connexion',
      firstConnection: 'Première connexion',
      failedAttempts: 'tentative(s) échouée(s) depuis votre dernière connexion',
      noFailedAttempts: 'Aucune tentative échouée',
    },
    // Statuts
    statuts: {
      cree: 'Créé',
      en_attente_ramassage: 'En attente de ramassage',
      ramasse: 'Ramassé',
      en_transit: 'En transit',
      arrive: 'Arrivé',
      affecte_livreur: 'Affecté à un livreur',
      en_livraison: 'En livraison',
      livre: 'Livré',
      livraison_echouee: 'Livraison échouée',
      retour_en_cours: 'Retour en cours',
      retourne: 'Retourné',
      annule: 'Annulé',
      en_dedouanement: 'En dédouanement',
      pret_retrait: 'Prêt au retrait',
      en_preparation: "En cours d'emballage",
      livreur_sur_place: 'Livreur sur place',
      injoignable: 'Client injoignable',
      reporte: 'Livraison reportée',
      adresse_introuvable: 'Adresse introuvable',
      absent: 'Client absent',
      refuse: 'Produit refusé',
      paiement_refuse: 'Paiement refusé',
    },
    etapesDouane: {
      sgs: 'SGS',
      guce: 'GUCE',
      liquidation: 'Liquidation',
      bon_a_enlever: 'Bon à enlever',
    },

	// Dans translations.fr :
	factures: {
	  title: 'Factures',
	  invoices: 'factures',
	  addInvoice: 'Créer une facture',
	  totalInvoices: 'Total Factures',
	  paid: 'Payées',
	  amountPaid: 'Montant Payé',
	  amountUnpaid: 'Montant Impayé',
	  search: 'Rechercher...',
	  allStatuses: 'Tous les statuts',
	  statutPaid: 'Payée',
	  statutPartial: 'Partiellement payée',
	  statutRefunded: 'Remboursée',
	  statutPending: 'En attente',
	  statutOverdue: 'En retard',
	  statutCancelled: 'Annulée',
	  invoiceNumber: 'N° Facture',
	  client: 'Client',
	  date: 'Date',
	  amount: 'Montant',
	  status: 'Statut',
	  actions: 'Actions',
	  view: 'Voir',
	  downloadPDF: 'Télécharger PDF',
	  markAsPaid: 'Marquer comme payée',
	  markAsUnpaid: 'Marquer comme impayée',
	  noInvoices: 'Aucune facture trouvée',
	  exportCSV: 'Export CSV',
	  exportSuccess: 'Export CSV réussi !',
	  exportError: 'Erreur lors de l\'export',
	  
	  // Détails
	  notFound: 'Facture introuvable',
	  backToList: 'Retour à la liste',
	  clientInfo: 'Informations Client',
	  clientName: 'Nom du client',
	  clientEmail: 'Email',
	  clientPhone: 'Téléphone',
	  clientAddress: 'Adresse',
	  linkedParcel: 'Colis lié',
	  linkedParcelId: 'ID du colis',
	  notes: 'Notes',
	  notesPlaceholder: 'Ajouter des notes...',
	  amounts: 'Montants',
	  baseAmount: 'Montant de base',
	  additionalFees: 'Frais additionnels',
	  discount: 'Réduction',
	  total: 'Total',
	  dates: 'Dates',
	  issueDate: 'Date d\'émission',
	  dueDate: 'Date d\'échéance',
	  paymentDate: 'Date de paiement',
	  paidOn: 'Payée le',
	  
	  // Création
	  createTitle: 'Créer une Facture',
	  createSubtitle: 'Remplissez les informations ci-dessous',
	  additionalInfo: 'Informations Complémentaires',
	  cancel: 'Annuler',
	  createButton: 'Créer la Facture',
	  creating: 'Création...',
	  createSuccess: 'Facture créée !',
	  createError: 'Erreur lors de la création',
	  
	  // Validation
	  clientNameRequired: 'Nom du client requis',
	  amountRequired: 'Montant requis',
	  amountPositive: 'Le montant doit être positif',
	  
	  // Actions
	  markedPaid: 'Facture marquée comme payée !',
	  markedUnpaid: 'Facture marquée comme impayée !',
	  updateError: 'Erreur lors de la mise à jour',
	  deleteSuccess: 'Facture supprimée !',
	  deleteError: 'Erreur lors de la suppression',
	  confirmDelete: 'Supprimer la facture',
	  exportExcel: 'Export Excel',
	  agingTitle: "Rapport d'ancienneté des créances",
	  agingOverdue: 'en retard',
	  agingBucket1: '1 – 30 jours',
	  agingBucket2: '31 – 60 jours',
	  agingBucket3: '61 – 90 jours',
	  agingBucket4: '+ 90 jours',
	  agingMore: 'autres',
	  agingDays: 'j',
	  pdfDownloadSuccess: 'PDF téléchargé !',
	  pdfDownloadError: 'Erreur lors du téléchargement',
	  exportExcelSuccess: 'Export Excel réussi !',
	  exportExcelError: "Erreur lors de l'export Excel",
	  paymentMethod: 'Mode de paiement',
	  paymentCash: 'Espèces',
	  paymentMethodOptional: 'Optionnel — peut être défini ou modifié ultérieurement',
	},
	tracking: {
	  title: 'Suivez votre colis',
	  subtitle: 'Entrez votre numéro de suivi pour voir où se trouve votre colis',
	  placeholder: 'CE-20260216-00001',
	  searchButton: 'Suivre mon colis',
	  searching: 'Recherche...',
	  notFound: 'Colis introuvable',
	  notFoundDesc: 'Vérifiez le numéro de suivi et réessayez',
	  managedBy: 'Géré par',
	  trackingNumber: 'Numéro de suivi',
	  recipient: 'Destinataire',
	  destination: 'Destination',
	  currentLocation: 'Localisation actuelle',
	  createdOn: 'Créé le',
	  timeline: 'Historique de livraison',
	  noHistory: 'Aucun historique disponible',
	  feature1Title: 'Suivi en temps réel',
	  feature1Desc: 'Suivez votre colis à chaque étape',
	  feature2Title: 'Mises à jour instantanées',
	  feature2Desc: 'Recevez des notifications automatiques',
	  feature3Title: 'Livraison garantie',
	  feature3Desc: 'Traçabilité complète de bout en bout',
	  footer: 'Tous droits réservés',
	  deliveryPrefTitle: 'Heure de livraison souhaitée',
	  deliveryPrefDesc: 'Indiquez quand vous préférez être livré, pour aider le livreur à organiser sa tournée.',
	  deliveryPrefDateLabel: 'Date souhaitée (facultatif)',
	  deliveryPrefSlotLabel: 'Créneau souhaité',
	  deliveryPrefSlotAny: 'Peu importe',
	  deliveryPrefNoteLabel: 'Note pour le livreur (facultatif)',
	  deliveryPrefNotePlaceholder: 'Ex. : appelez avant de passer, je suis au bureau...',
	  deliveryPrefSave: 'Enregistrer ma préférence',
	  deliveryPrefSaved: 'Préférence enregistrée — merci !',
	  deliveryPrefCurrent: 'Votre préférence actuelle',
	  deliveryPrefEdit: 'Modifier',
	},

    settings: {
      title: 'Paramètres',
      subtitle: 'Gérez les paramètres de votre entreprise',
      tabEntreprise: 'Entreprise',
      tabUsers: 'Utilisateurs',
      tabTarifs: 'Tarifs',
      companyInfo: 'Informations Entreprise',
      companyName: "Nom de l'entreprise",
      companyType: "Type d'entreprise",
      email: 'Email',
      phone: 'Téléphone',
      address: 'Adresse',
      devise: 'Devise',
      logo: 'Logo',
      currentLogo: 'Logo actuel',
      chooseLogo: 'Choisir un logo',
      uploadLogo: 'Télécharger le logo',
      uploading: 'Envoi en cours...',
      branding: 'Personnalisation',
      primaryColor: 'Couleur principale',
      secondaryColor: 'Couleur secondaire',
      brandingNote: "Ces couleurs seront utilisées dans l'interface de votre entreprise",
      subdomain: 'Sous-domaine',
      yourSubdomain: 'Votre sous-domaine personnalisé',
      save: 'Enregistrer',
      saving: 'Enregistrement...',
      updateSuccess: 'Paramètres mis à jour !',
      updateError: 'Erreur lors de la mise à jour',
      logoUploadSuccess: 'Logo téléchargé avec succès !',
      logoUploadError: 'Erreur lors du téléchargement du logo',
      legalInfo: 'Informations légales',
      niu: 'NIU (Numéro d\'Identification Unique)',
      niuNote: 'Affiché sur les factures PDF',
      registreCommerce: 'Registre de Commerce',
      paymentCodes: 'Codes marchands paiement mobile',
      paymentCodesNote: 'Ces codes seront affichés sur les factures PDF',
      codeMarchandOM: 'Code marchand Orange Money',
      codeMarchandOMNote: 'Affiché sur les factures payées par OM',
      codeMarchandMoMo: 'Code marchand MTN MoMo',
      codeMarchandMoMoNote: 'Affiché sur les factures payées par MoMo',
      tabSecurity: 'Sécurité',
      auditLog: "Journal d'audit",
      auditLogSubtitle: 'Historique de toutes les actions effectuées sur le compte',
      filterByAction: 'Filtrer par action',
      filterByEntity: 'Filtrer par entité',
      filterDateFrom: 'Du',
      filterDateTo: 'Au',
      allActions: 'Toutes les actions',
      allEntities: 'Toutes les entités',
      auditUser: 'Utilisateur',
      auditAction: 'Action',
      auditEntity: 'Entité',
      auditDate: 'Date',
      auditIp: 'IP',
      auditDetails: 'Détails',
      noAuditLogs: 'Aucun événement trouvé',
      loadingAudit: 'Chargement du journal...',
      actionLogin: 'Connexion',
      actionLoginFailed: 'Échec connexion',
      actionCreate: 'Création',
      actionUpdate: 'Modification',
      actionDelete: 'Suppression',
      actionStatusChange: 'Changement statut',
      actionExport: 'Export',
      securityInfo: 'Informations de sécurité',
      loginThrottle: 'Protection brute-force : 5 tentatives max / 5 min par IP',
      rateLimiting: 'Limite globale : 200 requêtes / minute par IP',
      encryptionNote: 'Mots de passe hashés avec bcrypt (12 rounds)',
    },

    reports: {
      title: 'Rapports & Statistiques',
      subtitle: 'Analysez les performances de votre entreprise',
      week: '7 jours',
      month: 'Ce mois',
      year: 'Cette année',
      totalParcels: 'Total Colis',
      delivered: 'Livrés',
      revenue: 'Revenus période',
      activeDelivery: 'Livreurs actifs',
      parcelsPerDay: 'Colis par jour',
      parcelsByStatus: 'Répartition par statut',
      inProgress: 'En cours',
      topDelivery: 'Top livreurs (historique)',
      deliveryPerson: 'Livreur',
      deliveries: 'Livraisons',
      performance: 'Performance des livreurs',
      deliveryRate: 'Taux de livraison',
      cancellationRate: "Taux d'annulation",
      avgDelay: 'Délai moyen livraison',
      inProgressCount: 'Colis en cours',
      topCities: 'Top villes destinataires',
      revenueByMonth: 'Revenus par mois',
      totalRevenue: 'Total revenus',
      bestMonth: 'Meilleur mois',
      avgMonthly: 'Moyenne mensuelle',
      exportCsv: 'Export CSV',
      exportExcel: 'Export Excel',
      exporting: 'Export...',
      exportSuccess: 'Export téléchargé !',
      exportError: "Erreur lors de l'export",
      noData: 'Aucune donnée',
      yearSelector: 'Année',
      successRate: 'Taux réussite',
      totalAssigned: 'Total assignés',
      rank: '#',
      workingDays: 'jours ouvrés',
      tabOverview: 'Vue générale',
      tabComparison: 'Comparaison',
      tabActivity: 'Activité',
      comparison: 'Comparaison avec la période précédente',
      currentPeriod: 'Période actuelle',
      previousPeriod: 'Période précédente',
      vsLastPeriod: 'vs période préc.',
      noPreviousData: 'Données insuffisantes pour la période précédente',
      funnel: 'Entonnoir de livraison',
      funnelDesc: 'Progression des colis par statut sur la période',
      heatmap: "Carte d'activité",
      heatmapDesc: 'Volume de création de colis par heure et jour de semaine',
      timeNight: 'Nuit (22h-6h)',
      timeMorning: 'Matin (6h-12h)',
      timeAfternoon: 'Après-midi (12h-18h)',
      timeEvening: 'Soir (18h-22h)',
      dowSun: 'Dim', dowMon: 'Lun', dowTue: 'Mar', dowWed: 'Mer',
      dowThu: 'Jeu', dowFri: 'Ven', dowSat: 'Sam',
      annuleColis: 'Colis annulés',
      comparisonChart: 'Évolution actuel vs précédent',
    },

    profile: {
      title: 'Mon Profil',
      tabProfile: 'Profil',
      tabPassword: 'Mot de passe',
      fullName: 'Nom complet',
      phone: 'Téléphone',
      email: 'Email',
      role: 'Rôle',
      saveProfile: 'Enregistrer le profil',
      saving: 'Enregistrement...',
      updateSuccess: 'Profil mis à jour !',
      updateError: 'Erreur lors de la mise à jour',
      currentPassword: 'Mot de passe actuel',
      newPassword: 'Nouveau mot de passe',
      confirmPassword: 'Confirmer le nouveau mot de passe',
      changePassword: 'Changer le mot de passe',
      changing: 'Changement...',
      passwordSuccess: 'Mot de passe modifié !',
      passwordError: 'Erreur lors du changement de mot de passe',
      passwordsNotMatch: 'Les mots de passe ne correspondent pas',
      nameRequired: 'Nom requis',
      currentPasswordRequired: 'Mot de passe actuel requis',
      newPasswordRequired: 'Nouveau mot de passe requis',
      passwordMin: 'Minimum 6 caractères',
    },

    users: {
      title: 'Gestion des Utilisateurs',
      addUser: 'Ajouter un utilisateur',
      newUser: 'Nouvel utilisateur',
      editUser: "Modifier l'utilisateur",
      fullName: 'Nom complet',
      email: 'Email',
      phone: 'Téléphone',
      role: 'Rôle',
      password: 'Mot de passe',
      status: 'Statut',
      active: 'Actif',
      inactive: 'Inactif',
      actions: 'Actions',
      search: 'Rechercher...',
      noUsers: 'Aucun utilisateur trouvé',
      save: 'Enregistrer',
      saving: 'Enregistrement...',
      createSuccess: 'Utilisateur créé !',
      updateSuccess: 'Utilisateur mis à jour !',
      deleteSuccess: 'Utilisateur supprimé !',
      toggleSuccess: 'Statut mis à jour !',
      error: 'Erreur',
      confirmDelete: 'Supprimer cet utilisateur ?',
      nameRequired: 'Nom requis',
      emailRequired: 'Email requis',
      roleRequired: 'Rôle requis',
      passwordRequired: 'Mot de passe requis',
    },

    tarifs: {
      title: 'Configuration des Tarifs',
      addTarif: 'Nouveau tarif',
      newTarif: 'Nouveau tarif',
      editTarif: 'Modifier le tarif',
      name: 'Nom du tarif',
      description: 'Description',
      basePrice: 'Prix de base',
      pricePerKg: 'Prix par kg',
      fragilePrice: 'Supplément fragile',
      insurancePrice: "Supplément assurance",
      isDefault: 'Tarif par défaut',
      setDefault: 'Définir par défaut',
      active: 'Actif',
      noTarifs: 'Aucun tarif configuré',
      save: 'Enregistrer',
      saving: 'Enregistrement...',
      createSuccess: 'Tarif créé !',
      updateSuccess: 'Tarif mis à jour !',
      deleteSuccess: 'Tarif supprimé !',
      defaultSet: 'Tarif défini par défaut !',
      error: 'Erreur',
      confirmDelete: 'Supprimer ce tarif ?',
      nameRequired: 'Nom requis',
      basePriceRequired: 'Prix de base requis',
    },

    notifications: {
      title: 'Notifications',
      subtitle: 'Gérez vos notifications et communications',
      allNotifications: 'Toutes les notifications',
      unread: 'Non lues',
      markAllRead: 'Tout marquer comme lu',
      noNotifications: 'Aucune notification',
      smsBalance: 'Crédits SMS',
      whatsappBalance: 'Crédits WhatsApp',
      recharge: 'Recharger',
      send: 'Envoyer',
      sendSms: 'Envoyer SMS',
      sendWhatsapp: 'Envoyer WhatsApp',
      recipient: 'Destinataire',
      message: 'Message',
      sending: 'Envoi...',
      sendSuccess: 'Notification envoyée !',
      sendError: "Erreur lors de l'envoi",
      credits: 'crédits',
    },

    wallet: {
      title: 'Portefeuille SMS',
      subtitle: 'Gérez vos crédits de communication',
      smsCredits: 'Crédits SMS',
      whatsappCredits: 'Crédits WhatsApp',
      recharge: 'Recharger',
      rechargeAmount: 'Montant à recharger',
      history: "Historique des transactions",
      noHistory: 'Aucune transaction',
      date: 'Date',
      type: 'Type',
      amount: 'Montant',
      balance: 'Solde',
      rechargeSuccess: 'Recharge effectuée !',
      rechargeError: 'Erreur lors de la recharge',
    },

    importColis: {
      title: 'Import en masse',
      subtitle: 'Importez plusieurs colis depuis un fichier Excel',
      downloadTemplate: 'Télécharger le template Excel',
      uploadTitle: 'Glissez-déposez votre fichier ici',
      uploadSubtitle: 'ou cliquez pour choisir un fichier Excel (.xlsx)',
      uploading: 'Import en cours...',
      importButton: 'Importer les colis',
      results: 'Résultats de l\'import',
      created: 'Colis créés',
      skipped: 'Lignes ignorées (vides)',
      errors: 'Erreurs',
      errorRow: 'Ligne',
      noErrors: 'Aucune erreur — import réussi !',
      importSuccess: 'Import terminé !',
      importError: 'Erreur lors de l\'import',
      fileRequired: 'Veuillez sélectionner un fichier',
      fileTypeError: 'Fichier Excel (.xlsx) requis',
      templateInfo: 'Le template contient les colonnes obligatoires (*) et optionnelles',
    },

    livreurApp: {
      title: 'Mes livraisons',
      subtitle: 'Gérez vos colis du jour',
      todayStats: 'Aujourd\'hui',
      delivered: 'Livrés',
      pending: 'En attente',
      inProgress: 'En cours',
      noDeliveries: 'Aucune livraison assignée',
      noItemsInTab: 'Aucun colis dans cet onglet',
      callClient: 'Appeler',
      callSender: 'Expéditeur',
      openMaps: 'Voir carte',
      updateStatus: 'Statut',
      uploadProof: 'Photo preuve',
      proofUploaded: 'Photo envoyée — statut mis à jour !',
      proofError: 'Erreur lors de l\'envoi de la photo',
      statusUpdated: 'Statut mis à jour !',
      statusError: 'Erreur lors de la mise à jour',
      noLinkedAccount: 'Votre compte livreur n\'est pas encore lié à un compte utilisateur. Contactez votre administrateur.',
      tabActifs: 'Actifs',
      tabLivres: 'Livrés',
      tabTous: 'Tous',
      localisationLabel: 'Localisation',
      localisationPlaceholder: 'Quartier, rue...',
      commentaireLabel: 'Commentaire',
      commentairePlaceholder: 'Note optionnelle...',
      confirmStatus: 'Confirmer',
      refresh: 'Actualiser',
    },

    clientPortal: {
      title: 'Portail Client',
      subtitle: 'Suivez vos colis facilement',
      enterPhone: 'Votre numéro de téléphone',
      phonePlaceholder: '+237600000000',
      sendCode: 'Envoyer le code',
      sending: 'Envoi...',
      codeSent: 'Code envoyé sur votre téléphone',
      enterCode: 'Code reçu par SMS',
      codePlaceholder: '123456',
      verify: 'Vérifier',
      verifying: 'Vérification...',
      resendCode: 'Renvoyer le code',
      codeExpires: 'Le code expire dans 5 minutes',
      myColis: 'Mes Colis',
      noColiFound: 'Aucun colis trouvé pour ce numéro',
      colisDetails: 'Détails du colis',
      trackingNumber: 'N° Suivi',
      status: 'Statut',
      destination: 'Destination',
      logout: 'Déconnexion',
      phoneRequired: 'Numéro de téléphone requis',
      codeRequired: 'Code requis',
      invalidCode: 'Code invalide ou expiré',
      sendError: 'Erreur lors de l\'envoi du code',
    },

    payments: {
      title: 'Paiement en ligne',
      subtitle: 'Payez votre facture via mobile money',
      chooseMethod: 'Choisir le mode de paiement',
      orangeMoney: 'Orange Money',
      mtnMomo: 'MTN MoMo',
      enterPhone: 'Numéro de téléphone',
      phonePlaceholder: '+237600000000',
      amount: 'Montant à payer',
      payNow: 'Payer maintenant',
      initiating: 'Initiation du paiement...',
      waitingConfirm: 'En attente de confirmation...',
      waitingDesc: 'Confirmez le paiement sur votre téléphone',
      success: 'Paiement réussi !',
      successDesc: 'La facture a été marquée comme payée',
      failed: 'Paiement échoué',
      failedDesc: 'Le paiement n\'a pas été confirmé',
      retry: 'Réessayer',
      close: 'Fermer',
      payButton: 'Payer en ligne',
      notConfigured: 'Paiement mobile non configuré pour ce tenant',
    },
  },
  en: {
    // Login
    login: {
      title: 'CEDO Trace',
      subtitle: 'Sign in to your account',
      email: 'Email',
      password: 'Password',
      submit: 'Sign In',
      noAccount: "Don't have an account?",
      register: 'Sign Up',
      success: 'Login successful!',
      error: 'Login error',
      emailRequired: 'Email required',
      emailInvalid: 'Invalid email',
      passwordRequired: 'Password required',
    },
    // Register
    register: {
      title: 'CEDO Trace',
      subtitle: 'Create your account',
      companyInfo: 'Company Information',
      personalInfo: 'Personal Information',
      accountInfo: 'Account Information',
      companyName: 'Company Name',
      companyType: 'Company Type',
      particulierOnlyNotice: 'Online self-registration creates an Individual account (single courier / express fleet). For a business account (e-commerce, transport, international freight...), please contact an administrator.',
      fullName: 'Full Name',
      phone: 'Phone',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      submit: 'Create Account',
      hasAccount: 'Already have an account?',
      login: 'Sign In',
      success: 'Account created successfully!',
      error: 'Creation error',
      companyNameRequired: 'Company name required',
      companyTypeRequired: 'Company type required',
      nameRequired: 'Full name required',
      phoneRequired: 'Phone required',
      emailRequired: 'Email required',
      emailInvalid: 'Invalid email',
      passwordRequired: 'Password required',
      passwordMin: 'Minimum 6 characters',
      confirmPasswordRequired: 'Confirmation required',
      passwordsNotMatch: 'Passwords do not match',
      types: {
        particulier: 'Individual (P2P)',
        ecommerce: 'Social seller (informal e-commerce)',
        ecommerce_structure: 'Structured e-merchant (SME / brand)',
        transport_interurbain: 'Interurban transport agency (coaches)',
        cargo_international: 'Freight forwarder / international cargo (air & sea)',
        transit_douane: 'Customs & transit firm',
        inter_pays: 'Cross-border carrier',
        transport_local: 'Urban delivery company & couriers',
        multisite_distribution: 'Multi-site / distribution company',
      },
    },
    // Dashboard
    dashboard: {
      welcome: 'Welcome',
      totalColis: 'Total Parcels',
      inTransit: 'In Transit',
      inDelivery: 'Out for Delivery',
      delivered: 'Delivered',
      recentParcels: 'Recent Parcels',
      tracking: 'Tracking #',
      recipient: 'Recipient',
      city: 'City',
      status: 'Status',
      amount: 'Amount',
      loading: 'Loading...',
      noParcels: 'No parcels yet',
      viewAll: 'View all',
    },
    // Create Colis
    createColis: {
      title: 'Create New Parcel',
      senderInfo: 'Sender Information',
      recipientInfo: 'Recipient Information',
      parcelDetails: 'Parcel Details',
      fullName: 'Full Name',
      phone: 'Phone',
      city: 'City',
      fullAddress: 'Full Address',
      description: 'Content Description',
      value: 'Value (FCFA)',
      weight: 'Weight (KG)',
      dimensions: 'Dimensions (LxWxH)',
      deliveryPerson: 'Delivery Person',
      deliveryPersonOptional: 'Delivery Person (optional)',
      assignLater: '-- Assign later --',
      assignNowOrLater: 'You can assign a delivery person now or later',
      allCities: 'All cities',
      cancel: 'Cancel',
      create: 'Create Parcel',
      success: 'Parcel created successfully! #',
      error: 'Creation error',
      required: 'required',
      nameRequired: 'Recipient name required',
      phoneRequired: 'Phone required',
      cityRequired: 'City required',
      descRequired: 'Description required',
    },
    // Colis Details
    colisDetails: {
      createdOn: 'Created on',
      updateStatus: 'Update status',
      assignDelivery: 'Assign delivery',
      changeDelivery: 'Change delivery',
      print: 'Print',
      download: 'Download',
      currentStatus: 'Current Status',
      notSpecified: 'Not specified',
      sender: 'Sender',
      recipient: 'Recipient',
      parcelDetails: 'Parcel Details',
      description: 'Description',
      value: 'Value',
      weight: 'Weight',
      dimensions: 'Dimensions',
      totalAmount: 'Total Amount',
      assignedDelivery: 'Assigned Delivery Person',
      change: 'Change',
      history: 'History',
      noHistory: 'No history available',
      by: 'By',
      updateStatusTitle: 'Update status',
      newStatus: 'New status',
      selectStatus: '-- Select --',
      comment: 'Comment (optional)',
      addComment: 'Add a comment...',
      confirm: 'Confirm',
      updating: 'Updating...',
      parcelNotFound: 'Parcel not found',
      backToList: 'Back to list',
    },
    // Livreurs
    livreurs: {
      title: 'Delivery Persons',
      deliveryPersons: 'delivery persons',
      addDelivery: 'Add delivery person',
      totalDelivery: 'Total Delivery Persons',
      active: 'Active',
      totalDeliveries: 'Total Deliveries',
      search: 'Search delivery person...',
      view: 'View',
      edit: 'Edit',
      delete: 'Delete',
      delivered: 'Delivered',
      failed: 'Failed',
      rating: 'Rating',
      newDelivery: 'New delivery person',
      editDelivery: 'Edit delivery person',
      fullName: 'Full Name',
      phone: 'Phone',
      email: 'Email',
      mainCity: 'Main City',
      isActive: 'Delivery person active',
      cancel: 'Cancel',
      create: 'Create',
      update: 'Update',
      saving: 'Saving...',
      noDeliveryFound: 'No delivery person found',
      confirmDelete: 'Delete',
      activeStatus: 'Active',
      inactiveStatus: 'Inactive',
      createSuccess: 'Delivery person created!',
      updateSuccess: 'Delivery person updated!',
      deleteSuccess: 'Delivery person deleted!',
      deleteError: 'Error deleting delivery person',
      saveError: 'Error saving delivery person',
    },
    // Livreur Details
    livreurDetails: {
      information: 'Information',
      telephone: 'Phone',
      email: 'Email',
      mainCity: 'Main City',
      memberSince: 'Member since',
      performance: 'Performance',
      successRate: 'Success Rate',
      averageRating: 'Average Rating',
      totalParcels: 'Total Parcels',
      delivered: 'Delivered',
      failed: 'Failed',
      assignedParcels: 'Assigned Parcels',
      noParcelsAssigned: 'No parcels assigned',
      modify: 'Edit',
      deliveryNotFound: 'Delivery person not found',
      backToList: 'Back to list',
      date: 'Date',
    },
    // Assign Delivery Modal
    assignDelivery: {
      title: 'Assign delivery person',
      currentDelivery: 'Current delivery person',
      unassign: 'Unassign',
      search: 'Search delivery person...',
      noDeliveryFound: 'No delivery person found',
      cancel: 'Cancel',
      assign: 'Assign',
      assigning: 'Assigning...',
      selectDelivery: 'Please select a delivery person',
      confirmUnassign: 'Unassign current delivery person?',
    },
    // Common
    common: {
      logout: 'Logout',
      dashboard: 'Dashboard',
      parcels: 'Parcels',
      createParcel: 'Create Parcel',
      tracking: 'Tracking',
      settings: 'Settings',
      reports: 'Reports',
      profile: 'My Profile',
      notifications: 'Notifications',
      users: 'Users',
      tarifs: 'Pricing',
      wallet: 'Wallet',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      create: 'Create',
      search: 'Search...',
      loading: 'Loading...',
      noData: 'No data',
      error: 'An error occurred',
      yes: 'Yes',
      no: 'No',
      active: 'Active',
      inactive: 'Inactive',
      actions: 'Actions',
      back: 'Back',
      listeColis: 'Parcel list',
      notifHistory: 'History & Send',
      notifTemplates: 'Templates',
      myProfile: 'My Profile',
      lastConnection: 'Last login',
      firstConnection: 'First login',
      failedAttempts: 'failed attempt(s) since your last login',
      noFailedAttempts: 'No failed attempts',
    },
    // Statuts
    statuts: {
      cree: 'Created',
      en_attente_ramassage: 'Awaiting Pickup',
      ramasse: 'Picked Up',
      en_transit: 'In Transit',
      arrive: 'Arrived',
      affecte_livreur: 'Assigned to Courier',
      en_livraison: 'Out for Delivery',
      livre: 'Delivered',
      livraison_echouee: 'Delivery Failed',
      retour_en_cours: 'Return in Progress',
      retourne: 'Returned',
      annule: 'Cancelled',
      en_dedouanement: 'Customs Clearance',
      pret_retrait: 'Ready for Pickup',
      en_preparation: 'Being Packed',
      livreur_sur_place: 'Courier On Site',
      injoignable: 'Customer Unreachable',
      reporte: 'Rescheduled',
      adresse_introuvable: 'Address Not Found',
      absent: 'Customer Absent',
      refuse: 'Product Rejected',
      paiement_refuse: 'Payment Refused',
    },
    etapesDouane: {
      sgs: 'SGS Inspection',
      guce: 'GUCE',
      liquidation: 'Duty Assessment',
      bon_a_enlever: 'Release Order',
    },
	factures: {
	  title: 'Invoices',
	  invoices: 'invoices',
	  addInvoice: 'Create invoice',
	  totalInvoices: 'Total Invoices',
	  paid: 'Paid',
	  amountPaid: 'Amount Paid',
	  amountUnpaid: 'Amount Unpaid',
	  search: 'Search...',
	  allStatuses: 'All statuses',
	  statutPaid: 'Paid',
	  statutPartial: 'Partially paid',
	  statutRefunded: 'Refunded',
	  statutPending: 'Pending',
	  statutOverdue: 'Overdue',
	  statutCancelled: 'Cancelled',
	  invoiceNumber: 'Invoice #',
	  client: 'Client',
	  date: 'Date',
	  amount: 'Amount',
	  status: 'Status',
	  actions: 'Actions',
	  view: 'View',
	  downloadPDF: 'Download PDF',
	  markAsPaid: 'Mark as paid',
	  markAsUnpaid: 'Mark as unpaid',
	  noInvoices: 'No invoices found',
	  exportCSV: 'Export CSV',
	  exportSuccess: 'CSV exported successfully!',
	  exportError: 'Export error',
	  
	  // Details
	  notFound: 'Invoice not found',
	  backToList: 'Back to list',
	  clientInfo: 'Client Information',
	  clientName: 'Client name',
	  clientEmail: 'Email',
	  clientPhone: 'Phone',
	  clientAddress: 'Address',
	  linkedParcel: 'Linked parcel',
	  linkedParcelId: 'Parcel ID',
	  notes: 'Notes',
	  notesPlaceholder: 'Add notes...',
	  amounts: 'Amounts',
	  baseAmount: 'Base amount',
	  additionalFees: 'Additional fees',
	  discount: 'Discount',
	  total: 'Total',
	  dates: 'Dates',
	  issueDate: 'Issue date',
	  dueDate: 'Due date',
	  paymentDate: 'Payment date',
	  paidOn: 'Paid on',
	  
	  // Creation
	  createTitle: 'Create Invoice',
	  createSubtitle: 'Fill in the information below',
	  additionalInfo: 'Additional Information',
	  cancel: 'Cancel',
	  createButton: 'Create Invoice',
	  creating: 'Creating...',
	  createSuccess: 'Invoice created!',
	  createError: 'Creation error',
	  
	  // Validation
	  clientNameRequired: 'Client name required',
	  amountRequired: 'Amount required',
	  amountPositive: 'Amount must be positive',
	  
	  // Actions
	  markedPaid: 'Invoice marked as paid!',
	  markedUnpaid: 'Invoice marked as unpaid!',
	  updateError: 'Update error',
	  deleteSuccess: 'Invoice deleted!',
	  deleteError: 'Delete error',
	  confirmDelete: 'Delete invoice',
	  exportExcel: 'Export Excel',
	  agingTitle: 'Accounts Receivable Aging Report',
	  agingOverdue: 'overdue',
	  agingBucket1: '1 – 30 days',
	  agingBucket2: '31 – 60 days',
	  agingBucket3: '61 – 90 days',
	  agingBucket4: '90+ days',
	  agingMore: 'more',
	  agingDays: 'd',
	  pdfDownloadSuccess: 'PDF downloaded!',
	  pdfDownloadError: 'Download error',
	  exportExcelSuccess: 'Excel exported successfully!',
	  exportExcelError: 'Excel export error',
	  paymentMethod: 'Payment method',
	  paymentCash: 'Cash',
	  paymentMethodOptional: 'Optional — can be set or updated later',
	},
	tracking: {
	  title: 'Track your parcel',
	  subtitle: 'Enter your tracking number to see where your parcel is',
	  placeholder: 'CE-20260216-00001',
	  searchButton: 'Track my parcel',
	  searching: 'Searching...',
	  notFound: 'Parcel not found',
	  notFoundDesc: 'Check the tracking number and try again',
	  managedBy: 'Managed by',
	  trackingNumber: 'Tracking number',
	  recipient: 'Recipient',
	  destination: 'Destination',
	  currentLocation: 'Current location',
	  createdOn: 'Created on',
	  timeline: 'Delivery timeline',
	  noHistory: 'No history available',
	  feature1Title: 'Real-time tracking',
	  feature1Desc: 'Track your parcel at every step',
	  feature2Title: 'Instant updates',
	  feature2Desc: 'Receive automatic notifications',
	  feature3Title: 'Guaranteed delivery',
	  feature3Desc: 'Complete end-to-end traceability',
	  footer: 'All rights reserved',
	  deliveryPrefTitle: 'Preferred delivery time',
	  deliveryPrefDesc: 'Let us know when you prefer to be delivered, so the courier can plan their route.',
	  deliveryPrefDateLabel: 'Preferred date (optional)',
	  deliveryPrefSlotLabel: 'Preferred time slot',
	  deliveryPrefSlotAny: 'No preference',
	  deliveryPrefNoteLabel: 'Note for the courier (optional)',
	  deliveryPrefNotePlaceholder: 'E.g.: call before arriving, I\'m at the office...',
	  deliveryPrefSave: 'Save my preference',
	  deliveryPrefSaved: 'Preference saved — thank you!',
	  deliveryPrefCurrent: 'Your current preference',
	  deliveryPrefEdit: 'Edit',
	},

    settings: {
      title: 'Settings',
      subtitle: 'Manage your company settings',
      tabEntreprise: 'Company',
      tabUsers: 'Users',
      tabTarifs: 'Pricing',
      companyInfo: 'Company Information',
      companyName: 'Company name',
      companyType: 'Company type',
      email: 'Email',
      phone: 'Phone',
      address: 'Address',
      devise: 'Currency',
      logo: 'Logo',
      currentLogo: 'Current logo',
      chooseLogo: 'Choose logo',
      uploadLogo: 'Upload logo',
      uploading: 'Uploading...',
      branding: 'Branding',
      primaryColor: 'Primary color',
      secondaryColor: 'Secondary color',
      brandingNote: 'These colors will be used in your company interface',
      subdomain: 'Subdomain',
      yourSubdomain: 'Your custom subdomain',
      save: 'Save',
      saving: 'Saving...',
      updateSuccess: 'Settings updated!',
      updateError: 'Update error',
      logoUploadSuccess: 'Logo uploaded successfully!',
      logoUploadError: 'Logo upload error',
      legalInfo: 'Legal Information',
      niu: 'Tax Identification Number (NIU)',
      niuNote: 'Displayed on PDF invoices',
      registreCommerce: 'Business Registry',
      paymentCodes: 'Mobile Payment Merchant Codes',
      paymentCodesNote: 'These codes will appear on PDF invoices',
      codeMarchandOM: 'Orange Money Merchant Code',
      codeMarchandOMNote: 'Shown on invoices paid via OM',
      codeMarchandMoMo: 'MTN MoMo Merchant Code',
      codeMarchandMoMoNote: 'Shown on invoices paid via MoMo',
      tabSecurity: 'Security',
      auditLog: 'Audit Log',
      auditLogSubtitle: 'History of all actions performed on the account',
      filterByAction: 'Filter by action',
      filterByEntity: 'Filter by entity',
      filterDateFrom: 'From',
      filterDateTo: 'To',
      allActions: 'All actions',
      allEntities: 'All entities',
      auditUser: 'User',
      auditAction: 'Action',
      auditEntity: 'Entity',
      auditDate: 'Date',
      auditIp: 'IP',
      auditDetails: 'Details',
      noAuditLogs: 'No events found',
      loadingAudit: 'Loading audit log...',
      actionLogin: 'Login',
      actionLoginFailed: 'Failed login',
      actionCreate: 'Create',
      actionUpdate: 'Update',
      actionDelete: 'Delete',
      actionStatusChange: 'Status change',
      actionExport: 'Export',
      securityInfo: 'Security information',
      loginThrottle: 'Brute-force protection: 5 attempts max / 5 min per IP',
      rateLimiting: 'Global rate limit: 200 requests / minute per IP',
      encryptionNote: 'Passwords hashed with bcrypt (12 rounds)',
    },

    reports: {
      title: 'Reports & Analytics',
      subtitle: 'Analyze your company performance',
      week: '7 days',
      month: 'This month',
      year: 'This year',
      totalParcels: 'Total Parcels',
      delivered: 'Delivered',
      revenue: 'Period revenue',
      activeDelivery: 'Active delivery persons',
      parcelsPerDay: 'Parcels per day',
      parcelsByStatus: 'Parcels by status',
      inProgress: 'In progress',
      topDelivery: 'Top delivery persons (all time)',
      deliveryPerson: 'Delivery person',
      deliveries: 'Deliveries',
      performance: 'Delivery person performance',
      deliveryRate: 'Delivery rate',
      cancellationRate: 'Cancellation rate',
      avgDelay: 'Avg. delivery time',
      inProgressCount: 'Parcels in progress',
      topCities: 'Top destination cities',
      revenueByMonth: 'Revenue by month',
      totalRevenue: 'Total revenue',
      bestMonth: 'Best month',
      avgMonthly: 'Monthly average',
      exportCsv: 'Export CSV',
      exportExcel: 'Export Excel',
      exporting: 'Exporting...',
      exportSuccess: 'Export downloaded!',
      exportError: 'Export error',
      noData: 'No data',
      yearSelector: 'Year',
      successRate: 'Success rate',
      totalAssigned: 'Total assigned',
      rank: '#',
      workingDays: 'working days',
      tabOverview: 'Overview',
      tabComparison: 'Comparison',
      tabActivity: 'Activity',
      comparison: 'Comparison with previous period',
      currentPeriod: 'Current period',
      previousPeriod: 'Previous period',
      vsLastPeriod: 'vs prev. period',
      noPreviousData: 'Insufficient data for the previous period',
      funnel: 'Delivery funnel',
      funnelDesc: 'Parcel progression by status over the period',
      heatmap: 'Activity heatmap',
      heatmapDesc: 'Parcel creation volume by hour and day of week',
      timeNight: 'Night (10pm-6am)',
      timeMorning: 'Morning (6am-12pm)',
      timeAfternoon: 'Afternoon (12pm-6pm)',
      timeEvening: 'Evening (6pm-10pm)',
      dowSun: 'Sun', dowMon: 'Mon', dowTue: 'Tue', dowWed: 'Wed',
      dowThu: 'Thu', dowFri: 'Fri', dowSat: 'Sat',
      annuleColis: 'Cancelled parcels',
      comparisonChart: 'Current vs previous period',
    },

    profile: {
      title: 'My Profile',
      tabProfile: 'Profile',
      tabPassword: 'Password',
      fullName: 'Full name',
      phone: 'Phone',
      email: 'Email',
      role: 'Role',
      saveProfile: 'Save profile',
      saving: 'Saving...',
      updateSuccess: 'Profile updated!',
      updateError: 'Update error',
      currentPassword: 'Current password',
      newPassword: 'New password',
      confirmPassword: 'Confirm new password',
      changePassword: 'Change password',
      changing: 'Changing...',
      passwordSuccess: 'Password changed!',
      passwordError: 'Password change error',
      passwordsNotMatch: 'Passwords do not match',
      nameRequired: 'Name required',
      currentPasswordRequired: 'Current password required',
      newPasswordRequired: 'New password required',
      passwordMin: 'Minimum 6 characters',
    },

    users: {
      title: 'User Management',
      addUser: 'Add user',
      newUser: 'New user',
      editUser: 'Edit user',
      fullName: 'Full name',
      email: 'Email',
      phone: 'Phone',
      role: 'Role',
      password: 'Password',
      status: 'Status',
      active: 'Active',
      inactive: 'Inactive',
      actions: 'Actions',
      search: 'Search...',
      noUsers: 'No users found',
      save: 'Save',
      saving: 'Saving...',
      createSuccess: 'User created!',
      updateSuccess: 'User updated!',
      deleteSuccess: 'User deleted!',
      toggleSuccess: 'Status updated!',
      error: 'Error',
      confirmDelete: 'Delete this user?',
      nameRequired: 'Name required',
      emailRequired: 'Email required',
      roleRequired: 'Role required',
      passwordRequired: 'Password required',
    },

    tarifs: {
      title: 'Pricing Configuration',
      addTarif: 'New pricing',
      newTarif: 'New pricing',
      editTarif: 'Edit pricing',
      name: 'Pricing name',
      description: 'Description',
      basePrice: 'Base price',
      pricePerKg: 'Price per kg',
      fragilePrice: 'Fragile surcharge',
      insurancePrice: 'Insurance surcharge',
      isDefault: 'Default pricing',
      setDefault: 'Set as default',
      active: 'Active',
      noTarifs: 'No pricing configured',
      save: 'Save',
      saving: 'Saving...',
      createSuccess: 'Pricing created!',
      updateSuccess: 'Pricing updated!',
      deleteSuccess: 'Pricing deleted!',
      defaultSet: 'Default pricing set!',
      error: 'Error',
      confirmDelete: 'Delete this pricing?',
      nameRequired: 'Name required',
      basePriceRequired: 'Base price required',
    },

    notifications: {
      title: 'Notifications',
      subtitle: 'Manage your notifications and communications',
      allNotifications: 'All notifications',
      unread: 'Unread',
      markAllRead: 'Mark all as read',
      noNotifications: 'No notifications',
      smsBalance: 'SMS Credits',
      whatsappBalance: 'WhatsApp Credits',
      recharge: 'Top up',
      send: 'Send',
      sendSms: 'Send SMS',
      sendWhatsapp: 'Send WhatsApp',
      recipient: 'Recipient',
      message: 'Message',
      sending: 'Sending...',
      sendSuccess: 'Notification sent!',
      sendError: 'Send error',
      credits: 'credits',
    },

    wallet: {
      title: 'SMS Wallet',
      subtitle: 'Manage your communication credits',
      smsCredits: 'SMS Credits',
      whatsappCredits: 'WhatsApp Credits',
      recharge: 'Top up',
      rechargeAmount: 'Amount to top up',
      history: 'Transaction history',
      noHistory: 'No transactions',
      date: 'Date',
      type: 'Type',
      amount: 'Amount',
      balance: 'Balance',
      rechargeSuccess: 'Top up successful!',
      rechargeError: 'Top up error',
    },

    importColis: {
      title: 'Bulk Import',
      subtitle: 'Import multiple parcels from an Excel file',
      downloadTemplate: 'Download Excel Template',
      uploadTitle: 'Drag & drop your file here',
      uploadSubtitle: 'or click to choose an Excel file (.xlsx)',
      uploading: 'Importing...',
      importButton: 'Import Parcels',
      results: 'Import Results',
      created: 'Parcels created',
      skipped: 'Skipped rows (empty)',
      errors: 'Errors',
      errorRow: 'Row',
      noErrors: 'No errors — import successful!',
      importSuccess: 'Import complete!',
      importError: 'Import error',
      fileRequired: 'Please select a file',
      fileTypeError: 'Excel file (.xlsx) required',
      templateInfo: 'The template contains mandatory (*) and optional columns',
    },

    livreurApp: {
      title: 'My Deliveries',
      subtitle: 'Manage your deliveries for the day',
      todayStats: 'Today',
      delivered: 'Delivered',
      pending: 'Pending',
      inProgress: 'In Progress',
      noDeliveries: 'No deliveries assigned',
      noItemsInTab: 'No parcels in this tab',
      callClient: 'Call',
      callSender: 'Sender',
      openMaps: 'Map',
      updateStatus: 'Status',
      uploadProof: 'Proof photo',
      proofUploaded: 'Photo uploaded — status updated!',
      proofError: 'Error uploading photo',
      statusUpdated: 'Status updated!',
      statusError: 'Update error',
      noLinkedAccount: 'Your delivery account is not linked to a user account. Contact your administrator.',
      tabActifs: 'Active',
      tabLivres: 'Delivered',
      tabTous: 'All',
      localisationLabel: 'Location',
      localisationPlaceholder: 'Neighborhood, street...',
      commentaireLabel: 'Comment',
      commentairePlaceholder: 'Optional note...',
      confirmStatus: 'Confirm',
      refresh: 'Refresh',
    },

    clientPortal: {
      title: 'Client Portal',
      subtitle: 'Track your parcels easily',
      enterPhone: 'Your phone number',
      phonePlaceholder: '+237600000000',
      sendCode: 'Send code',
      sending: 'Sending...',
      codeSent: 'Code sent to your phone',
      enterCode: 'SMS code received',
      codePlaceholder: '123456',
      verify: 'Verify',
      verifying: 'Verifying...',
      resendCode: 'Resend code',
      codeExpires: 'Code expires in 5 minutes',
      myColis: 'My Parcels',
      noColiFound: 'No parcels found for this number',
      colisDetails: 'Parcel details',
      trackingNumber: 'Tracking #',
      status: 'Status',
      destination: 'Destination',
      logout: 'Logout',
      phoneRequired: 'Phone number required',
      codeRequired: 'Code required',
      invalidCode: 'Invalid or expired code',
      sendError: 'Error sending code',
    },

    payments: {
      title: 'Online Payment',
      subtitle: 'Pay your invoice via mobile money',
      chooseMethod: 'Choose payment method',
      orangeMoney: 'Orange Money',
      mtnMomo: 'MTN MoMo',
      enterPhone: 'Phone number',
      phonePlaceholder: '+237600000000',
      amount: 'Amount to pay',
      payNow: 'Pay now',
      initiating: 'Initiating payment...',
      waitingConfirm: 'Waiting for confirmation...',
      waitingDesc: 'Confirm the payment on your phone',
      success: 'Payment successful!',
      successDesc: 'The invoice has been marked as paid',
      failed: 'Payment failed',
      failedDesc: 'The payment was not confirmed',
      retry: 'Retry',
      close: 'Close',
      payButton: 'Pay online',
      notConfigured: 'Mobile payment not configured for this tenant',
    },
  },
};

// Hook pour utiliser les traductions
export const useTranslation = () => {
  const { language } = useLanguage();
  return translations[language];
};